import { Alert, Button } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import * as Flags from "country-flag-icons/react/3x2";
import {
  CalendarDays,
  Clock3,
  Globe2,
  MapPin,
  UserPlus,
  Video,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import type {
  SchedulePublicAvailableSlot,
  SchedulePublicBookingDetails,
  SchedulePublicBookingDetailsAnswer,
  SchedulePublicBookingDetailsInvitee,
  SchedulePublicBookingLink,
  SchedulePublicJsonRecord,
  SchedulePublicJsonValue,
  SchedulePublicQuestion,
} from "@/api/scheduling/schedule-public.types";
import { CheckboxGroup } from "@/design-system/components/checkbox/Checkbox";
import { DatePicker } from "@/design-system/components/datePicker/datePicker";
import {
  Input,
  PhoneInput,
  TextAreaInput,
  type PhoneInputCountry,
} from "@/design-system/components/input/Input";
import { RadioGroup } from "@/design-system/components/radioButton/RadioButton";
import { Select } from "@/design-system/components/select/Select";
import { Toggle } from "@/design-system/components/toggle/Toggle";
import { colors } from "@/design-system/theme/colors";
import {
  PhonenumberCodes,
  formatPhoneNumber,
  parsePhoneNumberString,
  validatePhoneByCountry,
} from "@/utils/constants/regionCountryCode";
import {
  formatLocationLabel,
  formatSlotTime,
  getBrandColor,
  getDefaultLocation,
  getLocationProviderLogo,
  getPublicBookingGuestEmailErrors,
  isValidPublicBookingEmail,
  type PublicBookingTimeFormat,
} from "../utils";
import {
  PublicBookingHostsSummary,
  RescheduleTimeSummary,
} from "./EventDetailsPanel";

type QuestionValue = string | string[] | boolean;
export type PublicBookingQuestionValue = QuestionValue;
export type PublicBookingQuestionValueMap = Record<
  string,
  PublicBookingQuestionValue
>;
type QuestionErrorMap = Record<string, string>;
const PUBLIC_BOOKING_NAME_PATTERN = /^[A-Za-z ]+$/;
const PUBLIC_BOOKING_NAME_DISALLOWED_PATTERN = /[^A-Za-z ]/g;
const DEFAULT_PUBLIC_BOOKING_PHONE_COUNTRY_CODE = "+91";

export interface PublicBookingInviteeDetails {
  name: string;
  email: string;
  phone: string;
}

export interface PublicBookingQuestionSubmitPayload {
  answers: PublicBookingQuestionValueMap;
  invitee: PublicBookingInviteeDetails;
  guestEmails: string[];
  rescheduleReason?: string;
}

interface BookingQuestionsFormProps {
  bookingLink: SchedulePublicBookingLink;
  selectedDate: Dayjs;
  selectedSlot: SchedulePublicAvailableSlot;
  timezone: string;
  timeFormat: PublicBookingTimeFormat;
  isSubmitting: boolean;
  submissionError?: string | null;
  mode?: "booking" | "reschedule";
  existingBooking?: SchedulePublicBookingDetails | null;
  onBack: () => void;
  onConfirm: (payload: PublicBookingQuestionSubmitPayload) => void;
}

interface QuestionOption {
  label: string;
  value: string;
  disabled?: boolean;
}

const getQuestionKey = (question: SchedulePublicQuestion): string =>
  question.fieldKey || String(question.id);

const normalizeOptionValue = (value: SchedulePublicJsonValue): string => {
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return "";
};

const parseQuestionOptions = (
  options: SchedulePublicQuestion["options"],
): QuestionOption[] => {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options
      .map((option, index) => {
        if (option && typeof option === "object" && !Array.isArray(option)) {
          const optionRecord = option as SchedulePublicJsonRecord;
          const label =
            normalizeOptionValue(optionRecord.label) ||
            normalizeOptionValue(optionRecord.value) ||
            `Option ${index + 1}`;
          const value = normalizeOptionValue(optionRecord.value) || label;
          return {
            label,
            value,
            disabled: optionRecord.isActive === false,
          };
        }

        const value = normalizeOptionValue(option);
        return value ? { label: value, value } : null;
      })
      .filter((option): option is QuestionOption => Boolean(option));
  }

  if (typeof options === "object") {
    return Object.entries(options)
      .map(([key, option], index) => {
        if (option && typeof option === "object" && !Array.isArray(option)) {
          const optionRecord = option as SchedulePublicJsonRecord;
          const label =
            normalizeOptionValue(optionRecord.label) ||
            normalizeOptionValue(optionRecord.value) ||
            key ||
            `Option ${index + 1}`;
          return {
            label,
            value: normalizeOptionValue(optionRecord.value) || key || label,
            disabled: optionRecord.isActive === false,
          };
        }

        const label = normalizeOptionValue(option) || key;
        return { label, value: key || label };
      })
      .filter((option) => option.label);
  }

  return [];
};

const isEmailQuestion = (question: SchedulePublicQuestion): boolean => {
  const fieldKey = question.fieldKey.toLowerCase();
  const mappedField = String(question.mapToCrmField || "").toLowerCase();
  return (
    question.fieldType === "email" ||
    fieldKey.includes("email") ||
    mappedField === "contact.email"
  );
};

const isNameQuestion = (question: SchedulePublicQuestion): boolean => {
  const fieldKey = question.fieldKey.toLowerCase();
  const mappedField = String(question.mapToCrmField || "").toLowerCase();
  return (
    mappedField === "contact.name" ||
    fieldKey === "full_name" ||
    fieldKey === "name" ||
    fieldKey.includes("name")
  );
};

const sanitizePublicBookingName = (value: string): string =>
  value.replace(PUBLIC_BOOKING_NAME_DISALLOWED_PATTERN, "");

const getPublicBookingNameError = (value: string): string | null => {
  const name = value.trim();
  if (!name) return null;
  return PUBLIC_BOOKING_NAME_PATTERN.test(name)
    ? null
    : "Full name can only contain letters and spaces.";
};

const isPhoneQuestion = (question: SchedulePublicQuestion): boolean => {
  const fieldKey = question.fieldKey.toLowerCase();
  const fieldType = question.fieldType.toLowerCase();
  const mappedField = String(question.mapToCrmField || "").toLowerCase();
  return (
    fieldType === "phone" ||
    fieldType === "phone_number" ||
    fieldType === "phonenumber" ||
    mappedField === "contact.phone" ||
    fieldKey.includes("phone")
  );
};

const getPublicBookingPhoneParts = (
  value: string,
  selectedCountryCode?: string,
): { countryCode: string; nationalNumber: string } => {
  const trimmedValue = value.trim();
  const fallbackCountryCode =
    selectedCountryCode || DEFAULT_PUBLIC_BOOKING_PHONE_COUNTRY_CODE;

  if (!trimmedValue) {
    return { countryCode: fallbackCountryCode, nationalNumber: "" };
  }

  const parsed = parsePhoneNumberString(trimmedValue);
  return {
    countryCode: parsed.countryCode || fallbackCountryCode,
    nationalNumber: parsed.nationalNumber.replace(/\D/g, ""),
  };
};

const getPublicBookingPhoneError = (
  value: string,
  selectedCountryCode?: string,
): string | null => {
  const { countryCode, nationalNumber } = getPublicBookingPhoneParts(
    value,
    selectedCountryCode,
  );

  if (!nationalNumber) return null;

  const validation = validatePhoneByCountry(nationalNumber, countryCode);
  return validation.isValid
    ? null
    : validation.error || "Enter a valid phone number.";
};

const renderPhoneFlag = (countryCode: string) => {
  const FlagComponent = Flags[countryCode as keyof typeof Flags];
  if (FlagComponent) {
    return <FlagComponent className="h-4 w-5 rounded-sm object-cover" />;
  }
  return <span className="h-4 w-5 rounded-sm bg-gray-200" />;
};

const getInitialQuestionValue = (
  question: SchedulePublicQuestion,
): QuestionValue => {
  if (
    question.fieldType === "multi_select" ||
    question.fieldType === "checkbox"
  ) {
    return [];
  }
  if (question.fieldType === "boolean") return false;
  return "";
};

const getPrimaryInvitee = (
  booking?: SchedulePublicBookingDetails | null,
): SchedulePublicBookingDetailsInvitee | null => {
  if (!booking) return null;
  if (booking.primaryInvitee) return booking.primaryInvitee;
  if (booking.invitee) return booking.invitee;
  return booking.invitees?.[0] ?? null;
};

const normalizeExistingAnswerValue = (
  question: SchedulePublicQuestion,
  answer?: SchedulePublicBookingDetailsAnswer,
): QuestionValue => {
  const initialValue = getInitialQuestionValue(question);
  if (!answer) return initialValue;

  const jsonValue = answer.answerJson;
  const rawValue = answer.answerValue;

  if (question.fieldType === "multi_select" || question.fieldType === "checkbox") {
    if (Array.isArray(jsonValue)) return jsonValue.map(String);
    if (
      jsonValue &&
      typeof jsonValue === "object" &&
      !Array.isArray(jsonValue) &&
      Array.isArray((jsonValue as Record<string, unknown>).value)
    ) {
      return ((jsonValue as Record<string, unknown>).value as unknown[]).map(
        String,
      );
    }
    if (typeof rawValue === "string" && rawValue.trim()) {
      return rawValue.split(",").map((value) => value.trim()).filter(Boolean);
    }
    return [];
  }

  if (question.fieldType === "boolean") {
    if (typeof rawValue === "boolean") return rawValue;
    if (typeof rawValue === "string") {
      return rawValue.toLowerCase() === "true" || rawValue === "1";
    }
    return false;
  }

  if (
    jsonValue &&
    typeof jsonValue === "object" &&
    !Array.isArray(jsonValue) &&
    typeof (jsonValue as Record<string, unknown>).value !== "undefined"
  ) {
    return String((jsonValue as Record<string, unknown>).value ?? "");
  }

  return rawValue === null || typeof rawValue === "undefined"
    ? initialValue
    : String(rawValue);
};

const getExistingBookingQuestionAnswer = (
  question: SchedulePublicQuestion,
  booking?: SchedulePublicBookingDetails | null,
): SchedulePublicBookingDetailsAnswer | undefined =>
  booking?.answers?.find((answer) => {
    const questionId = answer.questionId;
    const fieldKey = answer.fieldKey?.trim();
    return (
      (typeof questionId !== "undefined" &&
        questionId !== null &&
        String(questionId) === String(question.id)) ||
      Boolean(fieldKey && fieldKey === question.fieldKey)
    );
  });

const getInitialAnswersFromBooking = (
  questions: SchedulePublicQuestion[],
  booking?: SchedulePublicBookingDetails | null,
): PublicBookingQuestionValueMap =>
  questions.reduce<PublicBookingQuestionValueMap>((nextAnswers, question) => {
    const key = getQuestionKey(question);
    const invitee = getPrimaryInvitee(booking);
    const existingAnswer = getExistingBookingQuestionAnswer(question, booking);
    let value = normalizeExistingAnswerValue(question, existingAnswer);

    if (!existingAnswer && invitee) {
      if (isNameQuestion(question)) value = invitee.name?.trim() || "";
      if (isEmailQuestion(question)) value = invitee.email?.trim() || "";
      if (isPhoneQuestion(question)) value = invitee.phone?.trim() || "";
    }

    nextAnswers[key] = value;
    return nextAnswers;
  }, {});

const getInitialPhoneCountryCodes = (
  questions: SchedulePublicQuestion[],
  answers: PublicBookingQuestionValueMap,
): Record<string, string> => {
  const nextCodes: Record<string, string> = {};
  questions.forEach((question) => {
    if (!isPhoneQuestion(question)) return;
    const key = getQuestionKey(question);
    const phoneParts = getPublicBookingPhoneParts(getStringValue(answers[key]));
    nextCodes[key] = phoneParts.countryCode;
  });
  return nextCodes;
};

const getInitialGuestEmails = (
  booking: SchedulePublicBookingDetails | null | undefined,
  canShowGuests: boolean,
  maxInvitees: number,
): string[] => {
  if (!canShowGuests) return [];
  return (booking?.invitees ?? [])
    .slice(1, maxInvitees)
    .map((invitee) => invitee.email?.trim() || "")
    .filter(Boolean);
};

const getStringValue = (value: QuestionValue | undefined): string =>
  typeof value === "string" ? value : "";

const getArrayValue = (value: QuestionValue | undefined): string[] =>
  Array.isArray(value) ? value : [];

const getBooleanValue = (value: QuestionValue | undefined): boolean =>
  typeof value === "boolean" ? value : false;

const getVisibleQuestions = (
  questions: SchedulePublicQuestion[],
): SchedulePublicQuestion[] =>
  [...questions]
    .filter((question) => question.isHidden !== true)
    .sort((first, second) => (first.sortOrder || 0) - (second.sortOrder || 0));

const getAnswerLabel = (question: SchedulePublicQuestion): string =>
  question.label || "Question";

const getInviteeEmailFromAnswers = (
  questions: SchedulePublicQuestion[],
  answers: PublicBookingQuestionValueMap,
): string => {
  const emailQuestion = questions.find(isEmailQuestion);
  if (!emailQuestion) return "";
  return getStringValue(answers[getQuestionKey(emailQuestion)]).trim();
};

const validateAnswers = (
  questions: SchedulePublicQuestion[],
  answers: PublicBookingQuestionValueMap,
  guestEmails: string[],
  maxInvitees: number,
  phoneCountryCodes: Record<string, string>,
): {
  errors: QuestionErrorMap;
  guestErrors: Record<number, string>;
  invitee: PublicBookingInviteeDetails;
} => {
  const errors: QuestionErrorMap = {};
  const guestErrors: Record<number, string> = {};
  const invitee: PublicBookingInviteeDetails = {
    name: "",
    email: "",
    phone: "",
  };

  questions.forEach((question) => {
    const key = getQuestionKey(question);
    const value = answers[key];
    const label = getAnswerLabel(question);
    const isEmpty =
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);

    if (question.isRequired && isEmpty) {
      errors[key] = `${label} is required.`;
      return;
    }

    if (isEmailQuestion(question)) {
      const email = getStringValue(value).trim();
      if (email) {
        invitee.email = email;
      }
      if (email && !isValidPublicBookingEmail(email)) {
        errors[key] = "Enter a valid email address.";
      }
    }

    if (isNameQuestion(question)) {
      const name = getStringValue(value).trim();
      invitee.name = name;
      const nameError = getPublicBookingNameError(name);
      if (nameError) {
        errors[key] = nameError;
      }
    }

    if (isPhoneQuestion(question)) {
      const phone = getStringValue(value).trim();
      invitee.phone = phone;
      const phoneError = getPublicBookingPhoneError(
        phone,
        phoneCountryCodes[key],
      );
      if (phoneError) {
        errors[key] = phoneError;
      }
    }
  });

  if (!invitee.email) {
    const emailQuestion = questions.find(isEmailQuestion);
    if (emailQuestion) {
      errors[getQuestionKey(emailQuestion)] = "Email address is required.";
    }
  }

  Object.assign(
    guestErrors,
    getPublicBookingGuestEmailErrors({
      guestEmails,
      inviteeEmail: invitee.email,
      maxInvitees,
    }),
  );

  return { errors, guestErrors, invitee };
};

const FieldHelp = ({ question }: { question: SchedulePublicQuestion }) => {
  if (!question.helpText?.trim()) return null;
  return (
    <div className="mt-1 text-xs" style={{ color: colors.textMuted }}>
      {question.helpText}
    </div>
  );
};

const FieldError = ({ error }: { error?: string }) => {
  if (!error) return null;
  return (
    <div className="mt-1 text-xs" style={{ color: colors.error }}>
      {error}
    </div>
  );
};

const QuestionLabel = ({ question }: { question: SchedulePublicQuestion }) => (
  <label
    className="mb-1.5 block text-sm font-semibold"
    style={{ color: colors.textDarkest }}
  >
    {getAnswerLabel(question)}{" "}
    {question.isRequired && <span style={{ color: colors.error }}>*</span>}
  </label>
);

export const BookingQuestionsForm = ({
  bookingLink,
  selectedDate,
  selectedSlot,
  timezone,
  timeFormat,
  isSubmitting,
  submissionError,
  mode = "booking",
  existingBooking,
  onBack,
  onConfirm,
}: BookingQuestionsFormProps) => {
  const isRescheduleMode = mode === "reschedule";
  const questions = useMemo(
    () => getVisibleQuestions(bookingLink.questions),
    [bookingLink.questions],
  );
  const initialAnswers = useMemo(
    () => getInitialAnswersFromBooking(questions, existingBooking),
    [questions, existingBooking],
  );
  const maxInvitees = Math.max(1, Number(bookingLink.maxInvitees) || 1);
  const canShowGuests =
    (bookingLink.eventTypeMode === "group" ||
      bookingLink.eventTypeMode === "collective") &&
    maxInvitees > 1;
  const initialPhoneCountryCodes = useMemo(
    () => getInitialPhoneCountryCodes(questions, initialAnswers),
    [initialAnswers, questions],
  );
  const initialGuestEmails = useMemo(
    () => getInitialGuestEmails(existingBooking, canShowGuests, maxInvitees),
    [canShowGuests, existingBooking, maxInvitees],
  );
  const [answers, setAnswers] = useState<PublicBookingQuestionValueMap>(
    () => initialAnswers,
  );
  const [errors, setErrors] = useState<QuestionErrorMap>({});
  const [phoneCountryCodes, setPhoneCountryCodes] = useState<
    Record<string, string>
  >(() => initialPhoneCountryCodes);
  const [guestEmails, setGuestEmails] =
    useState<string[]>(() => initialGuestEmails);
  const [guestErrors, setGuestErrors] = useState<Record<number, string>>({});
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [rescheduleReasonError, setRescheduleReasonError] =
    useState<string | null>(null);
  const [isAddGuestHovered, setIsAddGuestHovered] = useState(false);
  const location = getDefaultLocation(bookingLink.locations);
  const locationLabel = formatLocationLabel(location);
  const locationLogo = getLocationProviderLogo(location?.locationType);
  const brandColor = getBrandColor(bookingLink);
  const startLabel = formatSlotTime(selectedSlot.startTime, timeFormat);
  const endLabel = formatSlotTime(selectedSlot.endTime, timeFormat);
  const canAddGuest = canShowGuests && guestEmails.length < maxInvitees - 1;
  const phoneInputCountries = useMemo<PhoneInputCountry[]>(() => {
    const seenDialCodes = new Set<string>();
    return PhonenumberCodes.filter((country) => {
      if (seenDialCodes.has(country.dial_code)) {
        return false;
      }
      seenDialCodes.add(country.dial_code);
      return true;
    }).map((country) => ({
      code: country.code,
      dial_code: country.dial_code,
      name: country.name,
    }));
  }, []);

  const updateAnswer = (
    question: SchedulePublicQuestion,
    value: QuestionValue,
  ) => {
    const key = getQuestionKey(question);
    setAnswers((currentAnswers) => {
      const nextValue =
        isNameQuestion(question) && typeof value === "string"
          ? sanitizePublicBookingName(value)
          : value;
      const nextAnswers = { ...currentAnswers, [key]: nextValue };
      if (isEmailQuestion(question)) {
        setGuestErrors(
          getPublicBookingGuestEmailErrors({
            guestEmails,
            inviteeEmail: getStringValue(nextValue),
            maxInvitees,
          }),
        );
      }
      return nextAnswers;
    });
    setErrors((currentErrors) => {
      if (!currentErrors[key]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[key];
      return nextErrors;
    });
  };

  const updatePhoneAnswer = (
    question: SchedulePublicQuestion,
    nationalNumber: string,
    countryCode: string,
  ) => {
    const key = getQuestionKey(question);
    setPhoneCountryCodes((currentCountryCodes) => ({
      ...currentCountryCodes,
      [key]: countryCode,
    }));
    updateAnswer(
      question,
      nationalNumber
        ? formatPhoneNumber(countryCode, nationalNumber.replace(/\D/g, ""))
        : "",
    );
  };

  const handleConfirm = () => {
    const trimmedReason = rescheduleReason.trim();
    if (isRescheduleMode && trimmedReason.length > 500) {
      setRescheduleReasonError("Reason must be 500 characters or fewer.");
      return;
    }

    const validation = validateAnswers(
      questions,
      answers,
      guestEmails,
      maxInvitees,
      phoneCountryCodes,
    );
    setErrors(validation.errors);
    setGuestErrors(validation.guestErrors);
    if (
      Object.keys(validation.errors).length > 0 ||
      Object.keys(validation.guestErrors).length > 0
    ) {
      return;
    }
    onConfirm({
      answers,
      invitee: validation.invitee,
      guestEmails: guestEmails.map((email) => email.trim()).filter(Boolean),
      rescheduleReason: isRescheduleMode ? trimmedReason : undefined,
    });
  };

  const updateGuestEmail = (index: number, value: string) => {
    setGuestEmails((currentEmails) => {
      const nextEmails = currentEmails.map((email, emailIndex) =>
        emailIndex === index ? value : email,
      );
      setGuestErrors(
        getPublicBookingGuestEmailErrors({
          guestEmails: nextEmails,
          inviteeEmail: getInviteeEmailFromAnswers(questions, answers),
          maxInvitees,
        }),
      );
      return nextEmails;
    });
  };

  const removeGuestEmail = (index: number) => {
    setGuestEmails((currentEmails) => {
      const nextEmails = currentEmails.filter(
        (_email, emailIndex) => emailIndex !== index,
      );
      setGuestErrors(
        getPublicBookingGuestEmailErrors({
          guestEmails: nextEmails,
          inviteeEmail: getInviteeEmailFromAnswers(questions, answers),
          maxInvitees,
        }),
      );
      return nextEmails;
    });
  };

  const renderQuestionField = (question: SchedulePublicQuestion) => {
    const key = getQuestionKey(question);
    const value = answers[key];
    const options = parseQuestionOptions(question.options).filter(
      (option) => !option.disabled,
    );
    const placeholder = question.placeholder || "Enter your answer";

    if (isPhoneQuestion(question)) {
      const phoneParts = getPublicBookingPhoneParts(
        getStringValue(value),
        phoneCountryCodes[key],
      );

      return (
        <PhoneInput
          value={phoneParts.nationalNumber}
          countryCode={phoneParts.countryCode}
          defaultCountryCode={DEFAULT_PUBLIC_BOOKING_PHONE_COUNTRY_CODE}
          placeholder={question.placeholder || "Enter phone number"}
          error={errors[key]}
          countries={phoneInputCountries}
          renderFlag={renderPhoneFlag}
          searchable
          size="medium"
          onChange={(nationalNumber) =>
            updatePhoneAnswer(question, nationalNumber, phoneParts.countryCode)
          }
          onCountryChange={(country) =>
            updatePhoneAnswer(
              question,
              phoneParts.nationalNumber,
              country.dial_code,
            )
          }
        />
      );
    }

    if (question.fieldType === "textarea") {
      return (
        <TextAreaInput
          value={getStringValue(value)}
          placeholder={placeholder}
          rows={3}
          autoSize={{ minRows: 3, maxRows: 4 }}
          error={errors[key]}
          onChange={(event) => updateAnswer(question, event.target.value)}
        />
      );
    }

    if (question.fieldType === "select") {
      return (
        <Select
          value={getStringValue(value) || undefined}
          placeholder={question.placeholder || "Select an option"}
          options={options}
          error={errors[key]}
          allowClear
          onChange={(nextValue) =>
            updateAnswer(question, String(nextValue || ""))
          }
        />
      );
    }

    if (question.fieldType === "multi_select") {
      return (
        <Select
          mode="multiple"
          value={getArrayValue(value)}
          placeholder={question.placeholder || "Select options"}
          options={options}
          error={errors[key]}
          wrapTags
          onChange={(nextValue) =>
            updateAnswer(
              question,
              Array.isArray(nextValue) ? nextValue.map(String) : [],
            )
          }
        />
      );
    }

    if (question.fieldType === "radio") {
      return (
        <>
          <RadioGroup
            value={getStringValue(value)}
            direction="vertical"
            options={options}
            onChange={(event) =>
              updateAnswer(question, String(event.target.value))
            }
          />
          <FieldError error={errors[key]} />
        </>
      );
    }

    if (question.fieldType === "checkbox") {
      return (
        <>
          <CheckboxGroup
            value={getArrayValue(value)}
            direction="vertical"
            options={options}
            onChange={(nextValue) =>
              updateAnswer(
                question,
                nextValue.map((item) => String(item)),
              )
            }
          />
          <FieldError error={errors[key]} />
        </>
      );
    }

    if (question.fieldType === "boolean") {
      return (
        <>
          <Toggle
            checked={getBooleanValue(value)}
            onChange={(checked) => updateAnswer(question, checked)}
            label={question.placeholder || "Yes"}
          />
          <FieldError error={errors[key]} />
        </>
      );
    }

    if (question.fieldType === "date") {
      return (
        <DatePicker
          value={
            getStringValue(value) ? dayjs(getStringValue(value)) : undefined
          }
          placeholder={question.placeholder || "Select date"}
          error={errors[key]}
          onChange={(date) =>
            updateAnswer(question, date?.format("YYYY-MM-DD") ?? "")
          }
        />
      );
    }

    return (
      <Input
        type={
          question.fieldType === "email"
            ? "email"
            : question.fieldType === "number"
              ? "number"
              : "text"
        }
        value={getStringValue(value)}
        placeholder={placeholder}
        error={errors[key]}
        onChange={(event) => updateAnswer(question, event.target.value)}
      />
    );
  };

  return (
    <div className="grid min-h-0 grid-cols-1 lg:h-full lg:grid-cols-[320px_minmax(0,1fr)]">
      <aside
        className="flex min-h-0 flex-col border-b px-6 py-7 lg:border-b-0 lg:border-r"
        style={{ borderColor: colors.surfaceSubtle }}
      >
        <div className="shrink-0">
          <PublicBookingHostsSummary
            bookingLink={bookingLink}
            existingBooking={existingBooking}
          />
          <h1
            className="mb-4 text-[26px] font-semibold leading-tight"
            style={{ color: colors.textDarkest }}
          >
            {bookingLink.name}
          </h1>
        </div>
        {bookingLink.description ? (
          <div
            className="min-h-0 flex-1 overflow-y-auto pr-3 text-[15px] leading-6"
            style={{ color: colors.textDefault }}
          >
            {bookingLink.description}
          </div>
        ) : (
          <div className="flex-1" />
        )}

        <div
          className="mt-6 shrink-0 space-y-3.5 text-[15px]"
          style={{ color: colors.textDefault }}
        >
          {isRescheduleMode ? (
            <RescheduleTimeSummary
              booking={existingBooking}
              selectedDate={selectedDate}
              selectedSlot={selectedSlot}
              timeFormat={timeFormat}
            />
          ) : (
            <div className="flex items-start gap-3 leading-6">
              <CalendarDays size={19} style={{ color: colors.textStrong }} />
              <span>
                {selectedDate.format("dddd, MMMM D, YYYY")}
                <br />
                {startLabel} - {endLabel}
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 leading-6">
            <Clock3 size={19} style={{ color: colors.textStrong }} />
            <span>{bookingLink.durationMinutes}m</span>
          </div>
          <div className="flex items-center gap-3 leading-6">
            <span className="flex h-6.25 min-w-4.75 shrink-0 items-center justify-start">
              {locationLogo ? (
                <img
                  src={locationLogo}
                  alt={locationLabel}
                  className="h-6.25 w-auto max-w-24 shrink-0 object-contain object-left"
                  loading="lazy"
                  decoding="async"
                />
              ) : location?.locationType === "custom" ? (
                <MapPin size={19} style={{ color: colors.textStrong }} />
              ) : (
                <Video size={19} style={{ color: colors.textStrong }} />
              )}
            </span>
            <span>{locationLabel}</span>
          </div>
          <div className="flex items-center gap-3 leading-6">
            <Globe2 size={19} style={{ color: colors.textStrong }} />
            <span>{timezone}</span>
          </div>
        </div>
      </aside>

      <section className="flex min-h-0 flex-col px-6 py-7">
        <div className="mx-auto flex min-h-0 w-full max-w-156.25 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6 pr-1">
            {submissionError ? (
              <Alert
                type="error"
                showIcon
                message={
                  isRescheduleMode
                    ? "Unable to re-schedule this booking"
                    : "Unable to confirm this booking"
                }
                description={submissionError}
                className="rounded-lg"
              />
            ) : null}

            {questions.map((question) => (
              <div key={getQuestionKey(question)}>
                <QuestionLabel question={question} />
                {renderQuestionField(question)}
                {!errors[getQuestionKey(question)] && (
                  <FieldHelp question={question} />
                )}
              </div>
            ))}

            {isRescheduleMode ? (
              <div>
                <label
                  className="mb-1.5 block text-sm font-semibold"
                  style={{ color: colors.textDarkest }}
                >
                  Reason for re-schedule
                </label>
                <TextAreaInput
                  value={rescheduleReason}
                  placeholder="Add a reason"
                  rows={3}
                  autoSize={{ minRows: 3, maxRows: 4 }}
                  maxLength={500}
                  showCount
                  error={rescheduleReasonError || undefined}
                  onChange={(event) => {
                    setRescheduleReason(event.target.value);
                    setRescheduleReasonError(null);
                  }}
                />
              </div>
            ) : null}

            {canShowGuests && (
              <div className="pt-2">
                {guestEmails.length > 0 && (
                  <div
                    className="mb-2 text-sm font-semibold"
                    style={{ color: colors.textDarkest }}
                  >
                    Add guests
                  </div>
                )}
                <div className="space-y-2">
                  {guestEmails.map((email, index) => (
                    <Input
                      key={index}
                      type="email"
                      value={email}
                      placeholder="Email"
                      error={guestErrors[index]}
                      suffix={
                        <button
                          type="button"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full"
                          style={{ color: colors.textMuted }}
                          onClick={() => removeGuestEmail(index)}
                          aria-label="Remove guest"
                        >
                          <X size={15} />
                        </button>
                      }
                      onChange={(event) =>
                        updateGuestEmail(index, event.target.value)
                      }
                    />
                  ))}
                </div>
                {canAddGuest && (
                  <button
                    type="button"
                    className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: isAddGuestHovered
                        ? colors.primaryExtraLight
                        : colors.white,
                      borderColor: isAddGuestHovered
                        ? colors.primaryLight
                        : colors.surfaceSubtle,
                      color: colors.primary,
                    }}
                    onMouseEnter={() => setIsAddGuestHovered(true)}
                    onMouseLeave={() => setIsAddGuestHovered(false)}
                    onClick={() =>
                      setGuestEmails((currentEmails) => [...currentEmails, ""])
                    }
                  >
                    <UserPlus size={17} />
                    Add another
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            className="-mx-1 flex shrink-0 items-center justify-end gap-3 bg-white px-1 pt-4"
            style={{ borderTop: `1px solid ${colors.surfaceLight}` }}
          >
            <Button
              type="text"
              size="middle"
              disabled={isSubmitting}
              onClick={onBack}
              className="!h-10 !px-5"
            >
              Back
            </Button>
            <Button
              type="primary"
              size="middle"
              loading={isSubmitting}
              onClick={handleConfirm}
              className="!h-10 !rounded-lg !px-6"
              style={{ backgroundColor: brandColor, borderColor: brandColor }}
            >
              {isRescheduleMode ? "Re-schedule" : "Confirm"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingQuestionsForm;
